<template>
    <base-graphic
        :group-icon="groupIcon"
        :title-text="titleText"
        :subtitle-text="subtitleText"
        :loading="loading"
        :hire-resource="hireResource"
        :hire-resource-link="hireResourceLink"
        :no-data-message="noDataMessage"
        :error="error"
        :tooltip-link="tooltipLink"
        :tooltip-text="tooltipText"
    >
        <template #action-title>
            <slot name="action-title" />
        </template>

        <template #action-header>
            <slot name="action-header" />
        </template>

        <template #graphic-data>
            <canvas v-show="!loading && !error" ref="chartCanvas" />
        </template>
    </base-graphic>
</template>

<script>
    import Chart from "chart.js/auto"

    import baseGraphic from '../base-graphic'

    export default {
        name: "bar-chart-js",

        components: {
            baseGraphic
        },

        props: {
			/**
			 * Utilize para adicionar um icone no grafico
			 */
			groupIcon: {
				type: String,
				required: false,
				default: ''
			},
			/**
			 * Utilize para adicionar um titulo ao grafico
			 */
			titleText: {
				type: String,
				required: false,
				default: ''
			},
			/**
			 * Utilize para adicionar um subtitle ao grafico
			 */
			subtitleText: {
				type: String,
				required: false,
				default: ''
			},
			/**
			 * Utilize para adicionar um label ao grafico
			 */
            labels: {
                type: Array,
                required: true,
                default: () => []
            },
			/**
			 * Utilize para adicionar um os dados ao grafico
			 */
            datasets: {
                type: [Array, Object],
                required: true,
                default: () => []
            },
			/**
			 * Utilize para adicionar um loading a tela
			 */
			loading: {
				type: Boolean,
				required: false,
				default: false
			},
			/**
			 * Utilize para adicionar uma ancora para contratar o recurso
			 */
			hireResource: {
				type: Boolean,
				required: false,
				default: false
			},
			/**
			 * Link para a tela com as informações para a contratação do recurso
			 */
			hireResourceLink: {
				type: String,
				required: false,
				default: '#'
			},
			/**
			 * Utilize para adicionar um link ao tooltip
			 */
			tooltipLink: {
				type: String,
				required: false,
				default: ''
			},
			/**
			 * Mensagem a ser mostrada no tooltip
			 */
			tooltipText: {
				type: String,
				required: false,
				default: 'O gráfico abaixo é atualizado em tempo real de acordo com a data de atualização.'
			},
			/**
			 * Mensagem a ser mostrada quando não houver informações ou ocorrer um erro ao
			 * buscar os dados
			 */
			noDataMessage: {
				type: String,
				required: false,
				default: 'Não há dados a serem exibidos'
			},
			/**
			 * Define se houve um erro ao trazer os dados
			 */
			error: {
				type: Boolean,
				required: false,
				default: false
			}
        },

        data() {
            return {
                windowWidth: window.innerWidth,
                config: {},
                chart: null
            }
        },

        computed: {
            isMobile() {
                return this.windowWidth <= 767 // Por exemplo, consideramos abaixo de 768px como mobile
            }
        },

        watch: {
            datasets() {
                this.updateChart()
            },
            labels() {
                this.updateChart()
            },
            loading() {
                this.updateChart()
            }
        },

        created() {
            window.addEventListener('resize', this.handleResize)
        },

        mounted() {
            this.loadChart()
        },

        beforeDestroy() {
            window.removeEventListener('resize', this.handleResize)
        },

        methods: {
            /**
             * Carrega o gráfico de barras em linhas com os dados recebidos como parâmetros
             */
            loadChart() {
                const ctx = this.$refs.chartCanvas.getContext("2d")
                this.chart = new Chart(
                    ctx,
                    this.config = {
                        type: "bar",
                        data: {
                            labels: this.labels,
                            datasets: this.datasets
                        },
                        options: {
                            responsive: true,
                            maintainAspectRatio: false,
                            indexAxis: "x",
                            plugins: {
                                legend: {
                                    position: 'bottom',
                                    display: !this.isMobile,
                                    labels: {
                                        usePointStyle: true, // Exibe a legenda como forma oval
                                        padding: 20,
                                    }
                                },
                                tooltip: {
                                    backgroundColor: '#fff', // Cor de fundo do tooltip
                                    titleColor: '#4A586C', // Cor do texto do tooltip
                                    titleAlign: 'center',
                                    titleFont: {
                                        weight: 'regular'
                                    },
                                    bodyColor: '#000',
                                    borderColor: '#E6E6F2',
                                    borderWidth: 1,
                                    usePointStyle: true,
                                    displayColors: false,
                                    padding: 15,
                                    bodyAlign: 'center',
                                    bodyFont: {
                                        weight: 'bold',
                                        size: 13
                                    },
                                    callbacks: {
                                        label: (context) => {
                                            const value = context.parsed.y;
                                            return value
                                        },
                                        title: (tooltipItem) => {
                                            const label = tooltipItem[0].dataset.label[0]
                                            return label
                                        },
                                        labelPointStyle: () => {
                                            return {
                                                pointStyle: false,
                                            }
                                        },
                                        labelTextColor: function() {
                                            return "#4A586C"; // Cor do texto do label
                                        },
                                    },
                                },
                            },
                            scales: {
                                x: {
                                    grid: {
                                        display: false // Remove as linhas horizontais no eixo X
                                    },
                                    beginAtZero: true,
                                    stacked: true // Empilhar as barras horizontalmente
                                },
                                y: {
                                    stacked: true // Empilhar as barras verticalmente
                                }
                            },
                        }
                    }
                )
            },
            /**
             * Trata o evento de quando o tamanho da janela muda
             */
            handleResize() {
                this.windowWidth = window.innerWidth
            },
            /**
             * Atualiza os dados do chart de acordo com as props Datasets e Labels
             */
            updateChart() {
                this.chart.data.datasets = [...this.datasets]
                this.chart.data.labels = this.truncateLabel([...this.labels])
                this.chart.update()
            },

            truncateLabel(labels) {
                const maxLength = 20 // Comprimento máximo permitido

                return labels.map(element => {
                    if (element.length > maxLength) {
                        return element.substring(0, maxLength) + '...' // Adiciona '...' no final se exceder o limite
                    }
                    return element
                })
            }
        },
    }
</script>